import React, { useState } from 'react';
import {
  BookOpen, Scale, FileText, Users, ChevronDown, ChevronUp,
  ExternalLink, ClipboardList, GraduationCap, Landmark, Globe,
  HelpCircle, CheckSquare, ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// Grade → subject metadata. Defined alongside the component that consumes it so
// list view and detail view share one source of truth.
export const GRADE_META = {
  7: { subject: 'Kansas History', icon: Landmark, band: 'Middle School (6-8)' },
  8: { subject: 'U.S. History', icon: BookOpen, band: 'Middle School (6-8)' },
  9: { subject: 'World History', icon: Globe, band: 'High School (9-12)' },
  10: { subject: 'Modern World History', icon: Globe, band: 'High School (9-12)' },
  11: { subject: 'United States History', icon: BookOpen, band: 'High School (9-12)' },
  12: { subject: 'Government & Economics', icon: Scale, band: 'High School (9-12)' },
};

const DISCIPLINE_COLORS = {
  Civics: 'bg-blue-100 text-blue-800 border-blue-200',
  Economics: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Geography: 'bg-[#F9F8F4] text-[#14213D] border-[#EBE7E0]',
  History: 'bg-red-100 text-red-800 border-red-200',
};

/** Toggle-able section with chevron — used 7× inside AssessmentDetail. */
export function CollapsiblePanel({ title, icon: Icon, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#EBE7E0] rounded-lg bg-white overflow-hidden" data-testid={testId}>
      <button
        className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-[#F9F8F4] transition-colors"
        onClick={() => setOpen(!open)}
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="h-4 w-4 text-[#5A6577]" />}
          <span className="font-semibold text-[#14213D] text-sm">{title}</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-[#A0AEC0]" /> : <ChevronDown className="h-4 w-4 text-[#A0AEC0]" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-[#EBE7E0] pt-4">{children}</div>}
    </div>
  );
}

/** 4-level KSDE scoring rubric table. */
function RubricTable({ rubric }) {
  const categories = [
    { key: 'claim_thesis', label: 'Claim / Thesis' },
    { key: 'evidence', label: 'Evidence' },
    { key: 'reasoning', label: 'Reasoning' },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="rubric-table">
        <thead>
          <tr className="bg-[#F9F8F4]">
            <th className="px-3 py-2 text-left font-semibold text-[#2D3748] border border-[#EBE7E0]">Category</th>
            <th className="px-3 py-2 text-center font-semibold text-red-700 border border-[#EBE7E0] whitespace-nowrap">1 - Does Not Meet</th>
            <th className="px-3 py-2 text-center font-semibold text-[#14213D] border border-[#EBE7E0] whitespace-nowrap">2 - Approaches</th>
            <th className="px-3 py-2 text-center font-semibold text-blue-700 border border-[#EBE7E0] whitespace-nowrap">3 - Meets</th>
            <th className="px-3 py-2 text-center font-semibold text-emerald-700 border border-[#EBE7E0] whitespace-nowrap">4 - Exceeds</th>
          </tr>
        </thead>
        <tbody>
          {categories.map(cat => {
            const levels = rubric[cat.key] || [];
            const sorted = [...levels].sort((a, b) => a.score - b.score);
            return (
              <tr key={cat.key}>
                <td className="px-3 py-2 font-semibold text-[#14213D] border border-[#EBE7E0] bg-[#F9F8F4] align-top whitespace-nowrap">
                  {cat.label}
                </td>
                {sorted.map(level => (
                  <td key={level.score} className="px-3 py-2 text-xs text-[#5A6577] border border-[#EBE7E0] align-top">
                    {level.description}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Full detail view of one Kansas HGSS Classroom-Based Assessment.
 * Renders compelling question → staging → supporting questions → sources →
 * summative task → student directions → rubric → teacher notes.
 */
export default function AssessmentDetail({ assessment, onBack }) {
  const a = assessment;
  const meta = GRADE_META[a.grade_level] || {};

  return (
    <div className="space-y-5" data-testid="assessment-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-list-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to All Assessments
      </Button>

      {/* Title Section */}
      <Card className="p-6 border border-[#EBE7E0] bg-white" data-testid="assessment-header">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#14213D] bg-[#FDFBF7] px-2 py-0.5 rounded">
            Grade {a.grade_level} &mdash; {meta.subject}
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-[#5A6577] bg-[#F9F8F4] px-2 py-0.5 rounded">
            {meta.band}
          </span>
          <span className="text-xs text-[#A0AEC0]">Assessment {a.assessment_number}</span>
        </div>
        <h2 className="text-2xl font-bold text-[#14213D] mb-2" data-testid="assessment-title">{a.title}</h2>
        <p className="text-[#5A6577] text-sm">Unit: {a.unit_title}</p>

        {/* Compelling Question */}
        <div className="mt-5 p-4 bg-[#FDFBF7] border border-[#EBE7E0] rounded-lg">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#14213D] mb-1">Compelling Question</p>
          <p className="text-lg font-semibold text-[#14213D] italic" data-testid="compelling-question">
            &ldquo;{a.compelling_question}&rdquo;
          </p>
        </div>

        {/* Standards */}
        <div className="mt-4 flex flex-wrap gap-2">
          {(a.standards_addressed || []).map((s) => (
            <span key={s} className="text-xs bg-[#F9F8F4] text-[#2D3748] px-2.5 py-1 rounded-md border border-[#EBE7E0]">
              {s}
            </span>
          ))}
        </div>
      </Card>

      <CollapsiblePanel title="Staging the Question" icon={Users} defaultOpen={true} testId="staging-section">
        <p className="text-sm text-[#2D3748] leading-relaxed">{a.staging_the_question}</p>
      </CollapsiblePanel>

      <CollapsiblePanel title="Supporting Questions" icon={HelpCircle} defaultOpen={true} testId="supporting-questions-section">
        <div className="space-y-3">
          {(a.supporting_questions || []).map((sq, i) => (
            <div key={sq.question.slice(0, 30)} className="p-3 bg-[#F9F8F4] rounded-lg border border-[#EBE7E0]" data-testid={`supporting-q-${i}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-bold text-[#5A6577]">Q{i + 1}</span>
                <span className={`text-xs px-2 py-0.5 rounded border font-medium ${DISCIPLINE_COLORS[sq.discipline] || 'bg-[#F9F8F4] text-[#5A6577] border-[#EBE7E0]'}`}>
                  {sq.discipline}
                </span>
              </div>
              <p className="text-sm font-medium text-[#14213D]">{sq.question}</p>
              {sq.guidance && <p className="text-xs text-[#5A6577] mt-1">{sq.guidance}</p>}
            </div>
          ))}
        </div>
      </CollapsiblePanel>

      <CollapsiblePanel title="Featured Sources" icon={FileText} defaultOpen={true} testId="sources-section">
        <div className="space-y-3">
          {(a.featured_sources || []).map((src, i) => (
            <div key={src.title} className="p-3 bg-[#F9F8F4] rounded-lg border border-[#EBE7E0]" data-testid={`source-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${src.type === 'primary' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-[#EBE7E0] text-[#5A6577]'}`}>
                  {src.type}
                </span>
                <span className="text-sm font-semibold text-[#14213D]">{src.title}</span>
                {src.url && (
                  <a href={src.url} target="_blank" rel="noopener noreferrer" className="ml-auto text-[#14213D] hover:text-[#14213D]">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
              <p className="text-xs text-[#5A6577] mb-1">{src.description}</p>
              <p className="text-xs text-[#5A6577] italic">{src.analysis_prompt}</p>
            </div>
          ))}
        </div>
      </CollapsiblePanel>

      <CollapsiblePanel title="Summative Performance Task" icon={ClipboardList} defaultOpen={true} testId="task-section">
        <div>
          <p className="text-sm font-medium text-[#14213D] mb-2">
            {a.summative_task?.task_type || 'Persuasive/Argumentative Presentation or Essay'}
          </p>
          <p className="text-sm text-[#5A6577] mb-3">{a.summative_task?.description}</p>
          {a.summative_task?.requirements && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-[#2D3748] uppercase tracking-wider mb-1">Requirements</p>
              <ul className="space-y-1">
                {a.summative_task.requirements.map((r) => (
                  <li key={r.slice(0, 30)} className="text-sm text-[#5A6577] flex items-start gap-2">
                    <CheckSquare className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" /> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {a.summative_task?.format_options && (
            <div>
              <p className="text-xs font-semibold text-[#2D3748] uppercase tracking-wider mb-1">Format Options</p>
              <div className="flex flex-wrap gap-2">
                {a.summative_task.format_options.map((f) => (
                  <span key={f} className="text-xs bg-[#FDFBF7] text-[#14213D] px-2 py-1 rounded border border-[#EBE7E0]">{f}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsiblePanel>

      <CollapsiblePanel title="Student Directions" icon={GraduationCap} defaultOpen={false} testId="directions-section">
        <ol className="space-y-2">
          {(a.student_directions || []).map((d, i) => (
            <li key={`step-${i + 1}`} className="text-sm text-[#2D3748] flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#14213D] text-white text-xs font-bold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <span>{d.replace(/^Step \d+:\s*/i, '')}</span>
            </li>
          ))}
        </ol>
      </CollapsiblePanel>

      <CollapsiblePanel title="KSDE Scoring Rubric" icon={Scale} defaultOpen={false} testId="rubric-section">
        {a.rubric && <RubricTable rubric={a.rubric} />}
      </CollapsiblePanel>

      {a.teacher_notes && (
        <CollapsiblePanel title="Teacher Notes" icon={BookOpen} defaultOpen={false} testId="teacher-notes-section">
          <p className="text-sm text-[#2D3748] leading-relaxed">{a.teacher_notes}</p>
        </CollapsiblePanel>
      )}
    </div>
  );
}
